import React from 'react';
import { CheckCircle, XCircle, Clock, Globe, Camera, ExternalLink, Search, AlertTriangle, FileText, Layers, Shield, Code, Link, Zap } from 'lucide-react';
import { DetectionResult } from '../../../services/api';

export interface DetectionResultsProps {
  result: DetectionResult | null;
  onNewSearch: () => void;
  loading?: boolean;
  showNewSearchButton?: boolean;
}

// Helper function to convert technical evidence into user-friendly explanations
const getFriendlyEvidence = (evidence: any) => {
  const type = evidence.type;
  const description = evidence.description?.toLowerCase() || '';
  const content = evidence.content?.toLowerCase() || '';
  const location = evidence.location?.toLowerCase() || '';
  const locations = evidence.locations || [evidence.location];

  // Determine page type for context - handle multiple locations
  let pageType = 'website';
  let locationText = '';
  
  if (locations.length > 1) {
    // Multiple locations - show summary
    const uniquePageTypes = [...new Set(locations.map(loc => {
      const l = loc?.toLowerCase() || '';
      if (l.includes('checkout')) return 'checkout page';
      if (l.includes('product')) return 'product page';
      if (l.includes('homepage')) return 'homepage';
      if (l.includes('shop')) return 'shop page';
      return 'website';
    }))];
    
    if (uniquePageTypes.length === 1) {
      pageType = uniquePageTypes[0];
      locationText = `Found on ${pageType}`;
    } else {
      locationText = `Found on ${uniquePageTypes.slice(0, 2).join(' and ')}${uniquePageTypes.length > 2 ? ` and ${uniquePageTypes.length - 2} other pages` : ''}`;
    }
  } else {
    // Single location
    if (location.includes('checkout')) pageType = 'checkout page';
    else if (location.includes('product')) pageType = 'product page';
    else if (location.includes('homepage')) pageType = 'homepage';
    else if (location.includes('shop')) pageType = 'shop page';
    locationText = `Found on ${pageType}`;
  }

  // Analyze evidence and provide user-friendly explanation
  if (type === 'critical_integration' || description.includes('definitive') || description.includes('core tabby sdk') || content.includes('checkout.tabby.ai')) {
    return {
      icon: <Shield size={16} style={{ color: '#059669', marginTop: '2px' }} />,
      title: '🎯 Official Tabby Integration Found',
      explanation: 'We discovered Tabby\'s official payment system actively installed on this website. This means you can definitely use Tabby to make purchases here.',
      strength: 'Definitive Proof',
      pageType,
      locations: locationText,
      background: '#F0FDF4',
      badgeColor: '#DCFCE7',
      badgeTextColor: '#059669'
    };
  }

  if (type === 'high_confidence_integration' || description.includes('strong technical evidence') || description.includes('strong evidence')) {
    return {
      icon: <Code size={16} style={{ color: '#059669', marginTop: '2px' }} />,
      title: '💻 Tabby Code Integration Detected',
      explanation: 'The website\'s code contains specific Tabby payment features and configurations. This strongly indicates Tabby is set up and ready to use.',
      strength: 'Strong Evidence',
      pageType,
      locations: locationText,
      background: '#F0FDF4',
      badgeColor: '#DCFCE7',
      badgeTextColor: '#059669'
    };
  }

  if (type === 'official_domain' || description.includes('official tabby domain')) {
    return {
      icon: <Link size={16} style={{ color: '#059669', marginTop: '2px' }} />,
      title: '🌐 Connected to Tabby Servers',
      explanation: 'The website connects directly to Tabby\'s official servers, which is necessary for processing Tabby payments. This confirms an active integration.',
      strength: 'Official Connection',
      pageType,
      locations: locationText,
      background: '#F0FDF4',
      badgeColor: '#DCFCE7',
      badgeTextColor: '#059669'
    };
  }

  if (type === 'verified_pattern' || description.includes('verified indicator') || description.includes('verified')) {
    return {
      icon: <CheckCircle size={16} style={{ color: '#059669', marginTop: '2px' }} />,
      title: '✅ Tabby Payment Terms Found',
      explanation: 'We found text and elements that specifically mention Tabby payments, installments, or "buy now, pay later" options in the right context.',
      strength: 'Verified Content',
      pageType,
      locations: locationText,
      background: '#F0FDF4',
      badgeColor: '#DCFCE7',
      badgeTextColor: '#059669'
    };
  }

  if (type === 'contextual_verified') {
    return {
      icon: <FileText size={16} style={{ color: '#0891B2', marginTop: '2px' }} />,
      title: '📝 Payment Context Evidence',
      explanation: 'Found mentions of flexible payment options and installments in areas where customers would typically see payment choices.',
      strength: 'Contextual Match',
      pageType,
      locations: locationText,
      background: '#F0F9FF',
      badgeColor: '#E0F2FE',
      badgeTextColor: '#0891B2'
    };
  }

  if (type === 'negative_indicator') {
    return {
      icon: <AlertTriangle size={16} style={{ color: '#DC2626', marginTop: '2px' }} />,
      title: '⚠️ Competing Payment Service',
      explanation: `Found ${evidence.content}, which is a competitor to Tabby. This might mean the merchant uses alternative payment solutions instead.`,
      strength: 'Conflicting Info',
      pageType,
      locations: locationText,
      background: '#FEF2F2',
      badgeColor: '#FEE2E2',
      badgeTextColor: '#DC2626'
    };
  }

  if (type === 'weak_indicator' || description.includes('weak')) {
    return {
      icon: <Search size={16} style={{ color: '#F59E0B', marginTop: '2px' }} />,
      title: '🔍 Possible Tabby Reference',
      explanation: 'Found a mention that could be related to Tabby, but it\'s not clear if this refers to the payment service or something else.',
      strength: 'Weak Signal',
      pageType,
      locations: locationText,
      background: '#FFFBEB',
      badgeColor: '#FEF3C7',
      badgeTextColor: '#F59E0B'
    };
  }

  // Fallback for unknown types
  return {
    icon: <Zap size={16} style={{ color: '#6B7280', marginTop: '2px' }} />,
    title: '🔎 Detection Signal',
    explanation: 'Our analysis found this indicator while scanning for Tabby integration.',
    strength: 'Analysis Result',
    pageType,
    locations: locationText,
    background: '#F9FAFB',
    badgeColor: '#F3F4F6',
    badgeTextColor: '#6B7280'
  };
};

// Helper function to deduplicate evidence items
const deduplicateEvidence = (evidence: any[]) => {
  if (!evidence || evidence.length === 0) return [];

  const evidenceMap = new Map();
  
  evidence.forEach((item) => {
    // Create a unique key based on evidence type and main content
    const type = item.type || '';
    const description = item.description || '';
    const content = item.content || '';
    
    // Generate key for grouping similar evidence
    let groupKey = type;
    
    // For similar descriptions, group them together
    if (description.includes('Core Tabby SDK') || description.includes('DEFINITIVE')) {
      groupKey = 'definitive_integration';
    } else if (description.includes('Strong technical evidence') || description.includes('Strong evidence')) {
      groupKey = 'strong_integration';
    } else if (description.includes('Official Tabby domain')) {
      groupKey = 'official_domain';
    } else if (description.includes('Verified indicator') || type === 'verified_pattern') {
      groupKey = 'verified_pattern';
    } else if (type === 'contextual_verified') {
      groupKey = 'contextual_verified';
    } else if (type === 'negative_indicator') {
      groupKey = `negative_${content.toLowerCase()}`;
    } else if (type === 'weak_indicator' || description.includes('Weak')) {
      groupKey = 'weak_indicator';
    }
    
    const existing = evidenceMap.get(groupKey);
    
    if (existing) {
      // Merge locations for duplicate evidence
      const existingLocations = existing.locations || [existing.location];
      const newLocation = item.location;
      
      if (newLocation && !existingLocations.includes(newLocation)) {
        existing.locations = [...existingLocations, newLocation];
      }
      
      // Keep the evidence with higher priority/confidence
      if (shouldReplace(existing, item)) {
        evidenceMap.set(groupKey, { ...item, locations: existing.locations });
      }
    } else {
      evidenceMap.set(groupKey, item);
    }
  });
  
  return Array.from(evidenceMap.values());
};

// Helper to determine if we should replace existing evidence with new evidence
const shouldReplace = (existing: any, newItem: any) => {
  const priority = {
    'critical_integration': 5,
    'high_confidence_integration': 4,
    'official_domain': 3,
    'verified_pattern': 2,
    'contextual_verified': 1,
    'weak_indicator': 0,
    'negative_indicator': -1
  };
  
  const existingPriority = priority[existing.type] || 0;
  const newPriority = priority[newItem.type] || 0;
  
  return newPriority > existingPriority;
};

const DetectionResults: React.FC<DetectionResultsProps> = ({ 
  result, 
  onNewSearch,
  loading = false,
  showNewSearchButton = true
}) => {
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '32px 0' }}>
        <h3 style={{ 
          color: '#6B7785', 
          marginBottom: '16px',
          fontSize: '18px',
          fontWeight: 600,
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
        }}>
          Running deep website analysis...
        </h3>
        <p style={{ 
          color: '#9CA3AF', 
          marginBottom: '8px', 
          fontSize: '14px',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
        }}>
          Checking homepage, product pages, checkout flow, and payment sections
        </p>
        <p style={{ 
          color: '#9CA3AF', 
          marginBottom: '8px', 
          fontSize: '14px',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
        }}>
          Analyzing up to 12 pages for comprehensive detection
        </p>
        <p style={{ 
          color: '#9CA3AF', 
          fontSize: '14px',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
        }}>
          This may take 10-15 seconds for thorough results
        </p>
      </div>
    );
  }

  if (!result) {
    return null;
  }

  const { hasTabby, confidence, url, evidence, detectionMethod, processingTime, metadata } = result;

  const formatUrl = (url: string) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace('www.', '');
    } catch {
      return url;
    }
  };

  // Parse multi-page detection information
  const getDetectionInfo = () => {
    const detectionMethodLower = detectionMethod.toLowerCase();
    const isMultiPage = detectionMethodLower.includes('multi-page');
    
    if (isMultiPage) {
      const match = detectionMethod.match(/across (\d+) pages?: ([^]+)$/);
      if (match) {
        const pageCount = parseInt(match[1]);
        const pageTypes = match[2].split(', ').map(page => page.trim());
        return { isMultiPage: true, pageCount, pageTypes };
      }
    }
    
    return { isMultiPage: false, pageCount: 1, pageTypes: ['homepage'] };
  };

  const detectionInfo = getDetectionInfo();

  const getResultIcon = () => {
    if (hasTabby) {
      return <CheckCircle size={32} style={{ color: '#368510' }} />;
    }
    return <XCircle size={32} style={{ color: '#e31e18' }} />;
  };

  const getResultColor = () => hasTabby ? '#368510' : '#e31e18';
  const getResultBackground = () => hasTabby ? '#ddf7d0' : '#ffe2e2';

  const getConfidenceColor = () => {
    if (confidence >= 85) return '#368510';  // High confidence in analysis thoroughness
    if (confidence >= 75) return '#ec5503';  // Medium confidence in analysis thoroughness
    return '#e31e18';                        // Lower confidence in analysis completeness
  };

  return (
    <div style={{ width: '100%', maxWidth: '800px' }}>
      {/* Main Result Section - No card, just white section */}
      <div style={{ 
        backgroundColor: '#ffffff',
        padding: '32px',
        marginBottom: '24px'
      }}>
        {/* Result Header */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
          {getResultIcon()}
          <div style={{ marginLeft: '16px', flex: 1 }}>
            <h1 style={{ 
              color: '#000000', // Pure black for main heading
              fontSize: '24px',
              fontWeight: 600,
              lineHeight: '28px',
              margin: '0 0 8px 0',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
            }}>
              {hasTabby ? 'Tabby Available!' : 'Tabby Not Found'}
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Globe size={16} style={{ color: '#6B7785' }} />
              <span style={{ color: '#6B7785', fontSize: '16px' }}>
                {formatUrl(url)}
              </span>
            </div>
          </div>
        </div>

        {/* Result Description */}
        <div style={{ 
          backgroundColor: getResultBackground(),
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <p style={{ 
            color: getResultColor(),
            fontWeight: 500,
            fontSize: '16px',
            margin: '0 0 8px 0',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
          }}>
            {hasTabby 
              ? '✅ This website accepts Tabby payments'
              : '❌ This website does not currently accept Tabby payments'
            }
          </p>
          <p style={{ 
            color: '#6B7785',
            fontSize: '14px',
            margin: '0',
            lineHeight: '1.5',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
          }}>
            {hasTabby 
              ? `Tabby payment options were found${detectionInfo.isMultiPage ? ` across ${detectionInfo.pageCount} pages` : ''}. You can shop with flexible payment options and split your purchases into 4 interest-free installments.`
              : `Our ${detectionInfo.isMultiPage ? 'comprehensive multi-page' : 'detailed'} analysis${detectionInfo.isMultiPage ? ` of ${detectionInfo.pageCount} pages` : ''} found no Tabby integration. This merchant hasn't integrated Tabby yet - you may want to suggest Tabby as a payment option or look for alternative retailers.`
            }
          </p>
          {confidence >= 85 && (
            <p style={{ 
              color: '#059669',
              fontSize: '12px',
              margin: '8px 0 0 0',
              fontStyle: 'italic',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
            }}>
              ✅ High confidence: We thoroughly analyzed {detectionInfo.isMultiPage ? `${detectionInfo.pageCount} pages including` : 'key areas including'} checkout and product pages.
            </p>
          )}
        </div>

        {/* Detection Metadata */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            backgroundColor: confidence >= 85 ? '#D1FAE5' : confidence >= 75 ? '#FEF3C7' : '#FEE2E2', // Success, warning, or error background
            color: getConfidenceColor(),
            fontWeight: 500,
            fontSize: '14px',
            padding: '6px 12px',
            borderRadius: '16px'
          }}>
            <AlertTriangle size={16} />
            <span>{confidence}% Analysis Confidence</span>
          </div>
          
          {detectionInfo.isMultiPage && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: '#DBEAFE', // Info background
              color: '#2563EB', // Info text
              fontWeight: 500,
              fontSize: '14px',
              padding: '6px 12px',
              borderRadius: '16px'
            }}>
              <Layers size={16} />
              <span>{detectionInfo.pageCount} pages checked</span>
            </div>
          )}
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            backgroundColor: 'transparent',
            border: '1px solid #E5E7EB',
            color: '#6B7785',
            fontSize: '14px',
            padding: '6px 12px',
            borderRadius: '16px'
          }}>
            <Clock size={16} />
            <span>{(processingTime / 1000).toFixed(1)}s</span>
          </div>
          
          {metadata?.platform && (
            <div style={{
              backgroundColor: 'transparent',
              border: '1px solid #E5E7EB',
              color: '#6B7785',
              fontSize: '14px',
              padding: '6px 12px',
              borderRadius: '16px'
            }}>
              {metadata.platform}
            </div>
          )}
          
          {metadata?.region && (
            <div style={{
              backgroundColor: 'transparent',
              border: '1px solid #E5E7EB',
              color: '#6B7785',
              fontSize: '14px',
              padding: '6px 12px',
              borderRadius: '16px'
            }}>
              {metadata.region}
            </div>
          )}
        </div>

        {/* Pages Analyzed Section - for multi-page detection */}
        {detectionInfo.isMultiPage && (
          <>
            <div style={{ height: '1px', backgroundColor: '#E5E7EB', margin: '24px 0' }}></div>
            <h3 style={{ 
              color: '#1A202C',
              fontSize: '18px',
              fontWeight: 600,
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <FileText size={18} />
              Pages Analyzed
            </h3>
            
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
              {detectionInfo.pageTypes.map((pageType, index) => (
                <div
                  key={index}
                  style={{ 
                    border: '1px solid #2563EB',
                    color: '#2563EB',
                    backgroundColor: '#EFF6FF',
                    fontSize: '12px',
                    padding: '4px 8px',
                    borderRadius: '12px'
                  }}
                >
                  {pageType}
                </div>
              ))}
            </div>
            
            <p style={{ 
              color: '#6B7785', 
              fontSize: '14px',
              margin: '0',
              lineHeight: '1.5'
            }}>
              Our enhanced detection analyzes multiple page types to ensure comprehensive Tabby integration discovery, including product pages where payment options are most commonly displayed.
            </p>
          </>
        )}

        {/* Evidence Section */}
        {evidence && evidence.length > 0 && (
          <>
            <div style={{ height: '1px', backgroundColor: '#E5E7EB', margin: '24px 0' }}></div>
            <h3 style={{ 
              color: '#1A202C',
              fontSize: '18px',
              fontWeight: 600,
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Camera size={18} />
              How We Found Tabby
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {deduplicateEvidence(evidence).map((item, index) => {
                const friendlyEvidence = getFriendlyEvidence(item);
                return (
                  <div 
                    key={index}
                    style={{ 
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      padding: '16px',
                      backgroundColor: friendlyEvidence.background
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                      {friendlyEvidence.icon}
                      <div style={{ flex: 1 }}>
                        <p style={{ 
                          color: '#1A202C',
                          fontWeight: 600,
                          margin: '0 0 6px 0',
                          fontSize: '14px'
                        }}>
                          {friendlyEvidence.title}
                        </p>
                        <p style={{ 
                          color: '#4B5563',
                          fontSize: '13px',
                          margin: '0 0 8px 0',
                          lineHeight: '1.4'
                        }}>
                          {friendlyEvidence.explanation}
                        </p>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <span style={{
                            backgroundColor: friendlyEvidence.badgeColor,
                            color: friendlyEvidence.badgeTextColor,
                            fontSize: '11px',
                            padding: '3px 8px',
                            borderRadius: '12px',
                            fontWeight: 500
                          }}>
                            {friendlyEvidence.strength}
                          </span>
                          <span style={{ 
                            color: '#9CA3AF', 
                            fontSize: '11px'
                          }}>
                            {friendlyEvidence.locations || `Found on ${friendlyEvidence.pageType}`}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Detection Method */}
        <div style={{ marginTop: '24px' }}>
          <p style={{ 
            color: '#9CA3AF', 
            fontSize: '12px',
            margin: '0',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
          }}>
            Detection method: {detectionMethod}
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      {showNewSearchButton && (
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <button
            onClick={onNewSearch}
            style={{
              backgroundColor: '#000000', // Pure black primary button
              color: '#FFFFFF',
              fontWeight: 600,
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#1A202C';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#000000';
            }}
          >
            <Search size={18} />
            Check Another Website
          </button>

        {hasTabby && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              textDecoration: 'none',
              borderColor: '#3ECC71',
              color: '#3ECC71',
              backgroundColor: 'transparent',
              border: '1px solid #3ECC71',
              fontWeight: 500,
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#F0FDF4';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            <ExternalLink size={18} />
            Visit Website
          </a>
        )}

        {!hasTabby && (
          <a
            href="https://tabby.ai/en/business"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              textDecoration: 'none',
              borderColor: '#F59E0B',
              color: '#F59E0B',
              backgroundColor: 'transparent',
              border: '1px solid #F59E0B',
              fontWeight: 500,
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#FFFBEB';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            <ExternalLink size={18} />
            Suggest Tabby to Merchant
          </a>
        )}
        </div>
      )}
    </div>
  );
};

export default DetectionResults;
